import React, { useState, useEffect } from 'react';
import { Target, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import apiClient from '../services/apiClient';
import { formatDate, formatTime } from '../utils/dateUtils';
import ConfirmDialog from './ConfirmDialog';
import Toast from './Toast';
import useToast from '../hooks/useToast';

const isKnockout = (round) => round && round !== 'group_stage';

const isUndefinedTeam = (teamName) => {
  if (!teamName) return true;
  const t = teamName.toLowerCase();
  return t.includes('winner') || 
         t.includes('loser') || 
         t.includes('tbd') || 
         t.match(/^w\d+$/) || 
         t.match(/group/i) || 
         t.includes('1st') || 
         t.includes('2nd') || 
         t.includes('runner') || 
         t.match(/^[a-z]+\d+$/) || // like w73, ru101
         t.match(/^\d+[a-z]$/) || // like 1a, 2b
         t.match(/^\d+[a-z]+$/);  // like 3abcdf
};

function PredictionForm() {
  const [matches, setMatches] = useState([]);
  const [predictions, setPredictions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [submitting, setSubmitting] = useState({});

  // Error Modal State
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');

  // Resetting state
  const [isResetting, setIsResetting] = useState(false);
  const [isSavingAll, setIsSavingAll] = useState(false);

  // Confirm dialog state
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  // Toast state
  const { toasts, addToast, removeToast } = useToast();

  const fetchMatchesAndPredictions = async () => {
    try {
      setLoading(true);
      // Fetch matches and user's predictions in parallel
      const [matchesRes, predictionsRes] = await Promise.all([
        apiClient.get('/matches'),
        apiClient.get('/predictions')
      ]);

      const matchesData = matchesRes.data;
      const predictionsData = predictionsRes.data;

      // Sort matches by kickoff time (soonest first)
      const sortedMatches = matchesData.sort((a, b) =>
        new Date(a.kickoff_time) - new Date(b.kickoff_time)
      );

      setMatches(sortedMatches);

      // Create predictions object for easy lookup
      const userPredictions = {};
      predictionsData.forEach(pred => {
        userPredictions[pred.match_id] = {
          id: pred.id,
          home_score: pred.home_score !== null && pred.home_score !== undefined ? pred.home_score.toString() : '',
          away_score: pred.away_score !== null && pred.away_score !== undefined ? pred.away_score.toString() : '',
          winner_on_draw: pred.winner_on_draw || '',
          points: pred.points
        };
      });

      setPredictions(userPredictions);
      setError(null);
    } catch (err) {
      setError('Erro ao carregar partidas e palpites');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMatchesAndPredictions();
  }, []);

  const handleChange = (matchId, type, value) => {
    const updatedPrediction = {
      ...predictions[matchId],
      [type]: value
    };

    // Auto-clear winner_on_draw if scores are no longer equal
    if (type === 'home_score' || type === 'away_score') {
      const homeScore = type === 'home_score' ? value : updatedPrediction.home_score;
      const awayScore = type === 'away_score' ? value : updatedPrediction.away_score;
      if (homeScore !== '' && awayScore !== '' && parseInt(homeScore, 10) !== parseInt(awayScore, 10)) {
        updatedPrediction.winner_on_draw = '';
      }
    }

    setPredictions({
      ...predictions,
      [matchId]: updatedPrediction
    });
  };

  const handleSubmit = async (e, matchId) => {
    e.preventDefault();
    setSubmitting({ ...submitting, [matchId]: true });
    setSuccess(null);
    setError(null);

    const prediction = predictions[matchId];

    if (!prediction || prediction.home_score === undefined || prediction.home_score === '' || prediction.away_score === undefined || prediction.away_score === '') {
      setErrorModalMessage('Por favor, insira os placares');
      setIsErrorModalOpen(true);
      setSubmitting({ ...submitting, [matchId]: false });
      return;
    }

    const homeScoreNum = parseInt(prediction.home_score, 10);
    const awayScoreNum = parseInt(prediction.away_score, 10);

    if (isNaN(homeScoreNum) || isNaN(awayScoreNum)) {
      setErrorModalMessage('Placares inválidos');
      setIsErrorModalOpen(true);
      setSubmitting({ ...submitting, [matchId]: false });
      return;
    }

    // Validate winner_on_draw for knockout draws
    const match = matches.find(m => m.id === parseInt(matchId));
    const knockout = match && isKnockout(match.round);
    const isDrawPrediction = homeScoreNum === awayScoreNum;
    if (knockout && isDrawPrediction && !prediction.winner_on_draw) {
      setErrorModalMessage('Para empates em jogos eliminatórios, selecione quem avança');
      setIsErrorModalOpen(true);
      setSubmitting({ ...submitting, [matchId]: false });
      return;
    }

    const winnerOnDraw = knockout && isDrawPrediction ? prediction.winner_on_draw : null;

    try {
      if (prediction.id) {
        // Update existing prediction
        await apiClient.put(`/predictions/${prediction.id}`, {
          home_score: homeScoreNum,
          away_score: awayScoreNum,
          winner_on_draw: winnerOnDraw
        });
      } else {
        // Create new prediction
        const res = await apiClient.post('/predictions', {
          match_id: matchId,
          home_score: homeScoreNum,
          away_score: awayScoreNum,
          winner_on_draw: winnerOnDraw
        });
        
        // Update state with new ID
        setPredictions({
          ...predictions,
          [matchId]: {
            ...predictions[matchId],
            id: res.data.prediction?.id || res.data.id
          }
        });
      }

      setSuccess('Palpite salvo com sucesso!');
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Erro ao salvar palpite. A partida pode ter começado ou o prazo pode ter expirado.';
      setErrorModalMessage(errorMessage);
      setIsErrorModalOpen(true);
      console.error('Error saving prediction:', err);
    } finally {
      setSubmitting({ ...submitting, [matchId]: false });
    }
  };

  const handleResetPredictions = async () => {
    setIsResetting(true);
    setError(null);
    setSuccess(null);

    try {
      await apiClient.delete('/predictions/future');
      addToast('Palpites deletados com sucesso!', 'success');
      await fetchMatchesAndPredictions();
    } catch (err) {
      setErrorModalMessage('Erro ao resetar palpites. Tente novamente mais tarde.');
      setIsErrorModalOpen(true);
      console.error('Error resetting predictions:', err);
    } finally {
      setIsResetting(false);
      setResetConfirmOpen(false);
    }
  };

  const handleSaveAll = async () => {
    setIsSavingAll(true);
    setError(null);
    setSuccess(null);
    let successCount = 0;
    let errorCount = 0;

    // Filter valid predictions to save
    const predictionsToSave = Object.entries(predictions).filter(([matchId, pred]) => {
      if (!pred) return false;
      // Skip empty predictions
      if (pred.home_score === undefined || pred.home_score === '' || pred.away_score === undefined || pred.away_score === '') {
        return false;
      }
      // Skip locked/completed matches
      const match = matches.find(m => m.id === parseInt(matchId));
      if (!match) return false;
      const isLocked = new Date(match.kickoff_time) - new Date() <= 0 || match.status !== 'scheduled';
      if (isLocked) return false;

      return true;
    });

    if (predictionsToSave.length === 0) {
      setErrorModalMessage('Nenhum palpite preenchido e válido para salvar.');
      setIsErrorModalOpen(true);
      setIsSavingAll(false);
      return;
    }

    const updatedPredictions = { ...predictions };

    try {
      const results = await Promise.allSettled(
        predictionsToSave.map(async ([matchId, pred]) => {
          const homeScoreNum = parseInt(pred.home_score, 10);
          const awayScoreNum = parseInt(pred.away_score, 10);

          if (isNaN(homeScoreNum) || isNaN(awayScoreNum)) {
            throw new Error('Placares inválidos');
          }

          const match = matches.find(m => m.id === parseInt(matchId));
          const knockout = match && isKnockout(match.round);
          const isDrawPred = homeScoreNum === awayScoreNum;
          const winnerOnDraw = knockout && isDrawPred ? pred.winner_on_draw : null;

          if (pred.id) {
            await apiClient.put(`/predictions/${pred.id}`, {
              home_score: homeScoreNum,
              away_score: awayScoreNum,
              winner_on_draw: winnerOnDraw
            });
            return { matchId, id: pred.id };
          } else {
            const res = await apiClient.post('/predictions', {
              match_id: matchId,
              home_score: homeScoreNum,
              away_score: awayScoreNum,
              winner_on_draw: winnerOnDraw
            });
            return { matchId, id: res.data.prediction?.id || res.data.id };
          }
        })
      );

      results.forEach(result => {
        if (result.status === 'fulfilled') {
          successCount++;
          if (updatedPredictions[result.value.matchId]) {
            updatedPredictions[result.value.matchId].id = result.value.id;
          }
        } else {
          errorCount++;
          console.error('Error saving prediction:', result.reason);
        }
      });

      setPredictions(updatedPredictions);

      if (errorCount > 0) {
        setErrorModalMessage(`${successCount} palpites salvos. ${errorCount} palpites falharam.`);
        setIsErrorModalOpen(true);
      } else {
        setSuccess(`${successCount} palpites salvos com sucesso!`);
      }
    } catch (err) {
      setErrorModalMessage('Ocorreu um erro ao salvar todos os palpites.');
      setIsErrorModalOpen(true);
      console.error('Error saving all predictions:', err);
    } finally {
      setIsSavingAll(false);
    }
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
    setErrorModalMessage('');
  };

  if (loading) {
    return <div className="container">Carregando...</div>;
  }

  return (
    <div className="container overflow-x-hidden">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-primary">Fazer Palpites</h1>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <button
            onClick={handleSaveAll}
            disabled={isSavingAll || matches.length === 0}
            className="min-h-11 px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-dark focus:ring-4 focus:ring-green-300 transition-colors disabled:opacity-50 whitespace-nowrap"
          >
            {isSavingAll ? 'Salvando...' : 'Salvar Todos os Palpites'}
          </button>
          <button
            onClick={() => setResetConfirmOpen(true)}
            disabled={isResetting || matches.length === 0}
            className="min-h-11 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 focus:ring-4 focus:ring-red-300 transition-colors disabled:opacity-50 whitespace-nowrap"
          >
            {isResetting ? 'Resetando...' : 'Resetar Palpites'}
          </button>
        </div>
      </div>

      {success && <div className="alert alert-success mb-4">{success}</div>}
      {error && <div className="alert alert-error mb-4">{error}</div>}

      <div className="space-y-6">
        {matches.length === 0 ? (
          <div className="text-center">Nenhuma partida encontrada</div>
        ) : (
          matches.map((match) => {
            const isLocked = new Date(match.kickoff_time) - new Date() <= 0 || match.status !== 'scheduled';
            const isCompleted = match.status === 'completed';
            const isInProgress = match.status === 'in_progress';
            const isUndefined = isUndefinedTeam(match.home_team) || isUndefinedTeam(match.away_team);

            const getBorderClass = (type) => {
              if (isCompleted) {
                const userScore = predictions[match.id]?.[type];
                const realScore = match[type];
                if (userScore === undefined || userScore === '') return 'border-red-500 text-red-600 bg-red-50';
                return parseInt(userScore, 10) === realScore ? 'border-green-500 text-green-600 bg-green-50' : 'border-red-500 text-red-600 bg-red-50';
              }
              if (isInProgress) return 'border-yellow-500 text-yellow-600 bg-yellow-50';
              return 'border-blue-500 text-blue-600 bg-blue-50';
            };

            let outcomeBadge = null;
            if (isCompleted) {
              const userHome = predictions[match.id]?.home_score;
              const userAway = predictions[match.id]?.away_score;
              
              if (userHome !== undefined && userHome !== '' && userAway !== undefined && userAway !== '') {
                const uH = parseInt(userHome, 10);
                const uA = parseInt(userAway, 10);
                const rH = match.home_score;
                const rA = match.away_score;
                
                const isExact = uH === rH && uA === rA;
                const userOutcome = uH > uA ? 'home' : uH < uA ? 'away' : 'draw';
                const realOutcome = rH > rA ? 'home' : rH < rA ? 'away' : 'draw';
                const isOutcomeCorrect = userOutcome === realOutcome;
                
                // Backend computes points using current scoring rules
                const pts = predictions[match.id]?.points ?? 0;
                const ptsLabel = `+${pts} ${pts === 1 ? 'Ponto' : 'Pontos'}`;
                const matchIsKnockoutDraw = isKnockout(match.round) && rH === rA && match.winner_on_draw;
                const correctWinner = predictions[match.id]?.winner_on_draw === match.winner_on_draw;

                if (isExact) {
                  if (matchIsKnockoutDraw) {
                    const badges = [
                      <div key="draw" className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-800 text-xs font-bold rounded-full">Empate</div>,
                      <div key="exact" className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-primary text-xs font-bold rounded-full"><Target className="w-3.5 h-3.5" aria-label="Placar exato" /> Placar Exato</div>
                    ];
                    if (correctWinner) {
                      badges.push(<div key="winner" className="inline-flex items-center gap-1 px-2.5 py-1 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full">Acertou quem avança</div>);
                    } else {
                      badges.push(<div key="winner" className="inline-flex items-center gap-1 px-2.5 py-1 bg-orange-100 text-orange-700 text-xs font-bold rounded-full">Errou quem avança</div>);
                    }
                    badges.push(<div key="pts" className="inline-flex items-center gap-1 px-2.5 py-1 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full">{ptsLabel}</div>);
                    outcomeBadge = <div className="mt-2 flex flex-wrap gap-2">{badges}</div>;
                  } else {
                    outcomeBadge = (
                      <div className="mt-2 flex flex-wrap gap-2">
                        <div className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-primary text-xs font-bold rounded-full"><Target className="w-3.5 h-3.5" aria-label="Placar exato" /> Placar Exato</div>
                        <div className="inline-flex items-center gap-1 px-2.5 py-1 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full">{ptsLabel}</div>
                      </div>
                    );
                  }
                } else if (isOutcomeCorrect) {
                  if (matchIsKnockoutDraw) {
                    const badges = [
                      <div key="draw" className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-800 text-xs font-bold rounded-full">Empate</div>
                    ];
                    if (correctWinner) {
                      badges.push(<div key="winner" className="inline-flex items-center gap-1 px-2.5 py-1 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full">Acertou quem avança</div>);
                    } else {
                      badges.push(<div key="winner" className="inline-flex items-center gap-1 px-2.5 py-1 bg-orange-100 text-orange-700 text-xs font-bold rounded-full">Errou quem avança</div>);
                    }
                    badges.push(<div key="pts" className="inline-flex items-center gap-1 px-2.5 py-1 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full">{ptsLabel}</div>);
                    outcomeBadge = <div className="mt-2 flex flex-wrap gap-2">{badges}</div>;
                  } else {
                    outcomeBadge = (
                      <div className="mt-2 flex flex-wrap gap-2">
                        <div className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-800 text-xs font-bold rounded-full"><CheckCircle className="w-3.5 h-3.5" aria-label="Acertou" /> Acertou {realOutcome === 'draw' ? 'Empate' : 'Vencedor'}</div>
                        <div className="inline-flex items-center gap-1 px-2.5 py-1 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full">{ptsLabel}</div>
                      </div>
                    );
                  }
                } else {
                  outcomeBadge = (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <div className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-100 text-red-800 text-xs font-bold rounded-full"><XCircle className="w-3.5 h-3.5" aria-label="Errou" /> Errou</div>
                      <div className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-500 text-xs font-bold rounded-full">0 Pontos</div>
                    </div>
                  );
                }
              } else {
                 outcomeBadge = (
                   <div className="mt-2 flex flex-wrap gap-2">
                     <div className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-bold rounded-full"><AlertTriangle className="w-3.5 h-3.5" aria-label="Nenhum palpite" /> Nenhum palpite</div>
                     <div className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-500 text-xs font-bold rounded-full">0 Pontos</div>
                   </div>
                 );
              }
            }

            return (
              <div key={match.id} className="match-card">
                <div className="match-info">
                  <div className="match-teams break-words">
                    <span>{match.home_team_pt || match.home_team}</span>{' '}<span className="text-gray-500 font-normal">vs</span>{' '}<span>{match.away_team_pt || match.away_team}</span>
                  </div>
                  <div className="match-time">
                    {formatDate(match.kickoff_time)} às {formatTime(match.kickoff_time)}
                  </div>
                  {match.group_stage && (
                    <div className="text-sm">Grupo {match.group_stage}</div>
                  )}
                  {match.round && (
                    <div className="text-sm">{match.round}</div>
                  )}
                  {outcomeBadge}
                </div>

                <div className="flex flex-col items-center justify-center">
                  {isUndefined ? (
                    <div className="flex flex-col items-center">
                      <div className="text-sm text-secondary-dark font-semibold mb-2 bg-yellow-50 px-3 py-1 rounded-full border border-yellow-200">
                        Aguardando Definição das Seleções
                      </div>
                      <div className="text-xs text-gray-500 text-center max-w-[200px]">
                        Os palpites serão liberados após a definição dos times.
                      </div>
                    </div>
                  ) : !isLocked ? (
                    <form onSubmit={(e) => handleSubmit(e, match.id)} className="prediction-inputs">
                      <div className="flex items-center gap-3">
                        <input
                          type="number"
                          min="0"
                          max="99"
                          className="form-input prediction-score"
                          value={predictions[match.id]?.home_score ?? ''}
                          onChange={(e) => handleChange(match.id, 'home_score', e.target.value)}
                          placeholder="0"
                        />
                        <span className="text-2xl shrink-0">-</span>
                        <input
                          type="number"
                          min="0"
                          max="99"
                          className="form-input prediction-score"
                          value={predictions[match.id]?.away_score ?? ''}
                          onChange={(e) => handleChange(match.id, 'away_score', e.target.value)}
                          placeholder="0"
                        />
                      </div>
                      {/* Winner on draw selector for knockout matches */}
                      {isKnockout(match.round) &&
                        predictions[match.id]?.home_score !== '' &&
                        predictions[match.id]?.away_score !== '' &&
                        parseInt(predictions[match.id]?.home_score, 10) === parseInt(predictions[match.id]?.away_score, 10) && (
                        <div className="mt-2 flex flex-col items-center">
                          <span className="text-xs text-orange-600 font-semibold mb-1">Empate! Quem avança?</span>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              className={`px-3 py-1.5 text-sm rounded-lg border-2 font-medium transition-colors ${
                                predictions[match.id]?.winner_on_draw === 'home'
                                  ? 'bg-green-100 border-green-500 text-green-800'
                                  : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'
                              }`}
                              onClick={() => handleChange(match.id, 'winner_on_draw', 'home')}
                            >
                              {match.home_team_pt || match.home_team}
                            </button>
                            <button
                              type="button"
                              className={`px-3 py-1.5 text-sm rounded-lg border-2 font-medium transition-colors ${
                                predictions[match.id]?.winner_on_draw === 'away'
                                  ? 'bg-green-100 border-green-500 text-green-800'
                                  : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'
                              }`}
                              onClick={() => handleChange(match.id, 'winner_on_draw', 'away')}
                            >
                              {match.away_team_pt || match.away_team}
                            </button>
                          </div>
                        </div>
                      )}
                      <button
                        type="submit"
                        className="btn btn-primary w-full sm:w-auto"
                        disabled={submitting[match.id]}
                      >
                        {submitting[match.id] ? 'Salvando...' : 'Salvar'}
                      </button>
                    </form>
                  ) : (
                    <div className="flex flex-col items-center">
                      <div className="text-sm text-gray-500 font-semibold mb-2">
                        {isCompleted ? 'Seu Palpite' : isInProgress ? 'Em Andamento' : 'Palpite Fechado'}
                      </div>
                      <div className="flex items-center gap-3 sm:gap-4 text-xl font-bold">
                        <div className={`w-14 h-14 shrink-0 flex items-center justify-center border-2 rounded-lg ${getBorderClass('home_score')}`}>
                          {predictions[match.id]?.home_score !== undefined && predictions[match.id]?.home_score !== '' ? predictions[match.id]?.home_score : '-'}
                        </div>
                        <span className="text-2xl text-gray-500 shrink-0">X</span>
                        <div className={`w-14 h-14 shrink-0 flex items-center justify-center border-2 rounded-lg ${getBorderClass('away_score')}`}>
                          {predictions[match.id]?.away_score !== undefined && predictions[match.id]?.away_score !== '' ? predictions[match.id]?.away_score : '-'}
                        </div>
                      </div>
                      {predictions[match.id]?.winner_on_draw && (
                        <div className="mt-1 text-xs text-center text-orange-600 font-medium">
                          Avança: {predictions[match.id]?.winner_on_draw === 'home'
                            ? (match.home_team_pt || match.home_team)
                            : (match.away_team_pt || match.away_team)}
                        </div>
                      )}
                    </div>
                  )}

                  {isCompleted && (
                    <div className="mt-4 flex flex-col items-center">
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Placar Real</span>
                      <div className="flex items-center gap-2 font-bold text-lg bg-gray-100 px-4 py-1.5 rounded-full text-gray-800 shadow-sm border border-gray-200">
                        <span>{match.home_score}</span>
                        <span className="text-gray-500">X</span>
                        <span>{match.away_score}</span>
                      </div>
                      {match.winner_on_draw && (
                        <span className="text-xs text-orange-600 font-medium mt-1">
                          {match.winner_on_draw === 'home'
                            ? (match.home_team_pt || match.home_team)
                            : (match.away_team_pt || match.away_team)} avança nos pênaltis
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Error Modal */}
      {isErrorModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-black/50 backdrop-blur-sm">
          <div className="relative w-full max-w-sm p-4 mx-auto">
            <div className="bg-white rounded-xl shadow-xl overflow-hidden">
              <div className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Atenção</h3>
                <p className="text-sm text-gray-500 mb-6">{errorModalMessage}</p>
                <button
                  onClick={closeErrorModal}
                  className="w-full px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:ring-4 focus:ring-red-300 transition-colors"
                >
                  Entendi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={resetConfirmOpen}
        title="Resetar Palpites"
        message="Tem certeza que deseja resetar todos os palpites de partidas que ainda não aconteceram? Esta ação não pode ser desfeita."
        confirmLabel="Resetar"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={handleResetPredictions}
        onCancel={() => setResetConfirmOpen(false)}
      />

      <Toast toasts={toasts} removeToast={removeToast} />
    </div>
  );
}

export default PredictionForm;